import Papa from 'papaparse';
import type {
  ColumnGroup,
  TableColumn,
  TableRow,
} from '@data/components/basic/Table/types';
import { formatTableData } from './format-table-data';

export function toSnakeCase(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Build a CSV from flat table columns + rows and trigger a browser download.
 *
 * - Skips `id` columns (kept in the input so row indexes still align with the API)
 * - When `columnPrefixes` is provided, headers are `<prefix>_<column>` in
 *   snake_case. The prefixes describe non-id columns and are consumed via
 *   their `colSpan`.
 * - Formats cell values via `formatTableData` so the CSV matches the UI.
 */
export function exportTableToCSV(
  columns: TableColumn[],
  rows: TableRow[],
  filename: string,
  columnPrefixes?: ColumnGroup[]
) {
  // Pair each column with its original index so row cells line up correctly.
  const renderColumns = columns
    .map((col, originalIdx) => ({ col, originalIdx }))
    .filter(({ col }) => col.type !== 'id');

  const exportColumns: {
    rowIndex: number;
    header: string;
    type: TableColumn['type'];
  }[] = [];

  if (columnPrefixes && columnPrefixes.length > 0) {
    let i = 0;
    columnPrefixes.forEach((prefix) => {
      const prefixSnake = toSnakeCase(prefix.label);
      for (let n = 0; n < prefix.colSpan; n += 1) {
        const entry = renderColumns[i];
        i += 1;
        if (entry) {
          const colSnake = toSnakeCase(entry.col.label);
          exportColumns.push({
            rowIndex: entry.originalIdx,
            header: prefixSnake ? `${prefixSnake}_${colSnake}` : colSnake,
            type: entry.col.type,
          });
        }
      }
    });
  } else {
    renderColumns.forEach(({ col, originalIdx }) => {
      exportColumns.push({
        rowIndex: originalIdx,
        header: toSnakeCase(col.label),
        type: col.type,
      });
    });
  }

  const fields = exportColumns.map((column) => column.header);
  const data = rows.map((row) =>
    exportColumns.map((column) =>
      formatTableData(row[column.rowIndex], column.type, true)
    )
  );

  // `{ fields, data }` form ensures the header row is always emitted, even
  // when `data` is empty
  const csv = Papa.unparse({ fields, data }, { escapeFormulae: true });
  // Necessary for Excel to be able to read special chars
  const UTF8_BOM = '\uFEFF';
  const blob = new Blob([UTF8_BOM, csv], {
    type: 'text/csv;charset=utf-8;',
  });

  const url = URL.createObjectURL(blob);

  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}
